/* eslint-disable @typescript-eslint/no-var-requires */

/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import JSZip from "jszip";
window.JSZip = JSZip;

require("datatables.net-bs5")(window, $);
require("datatables.net-buttons-bs5")(window, $);
require("datatables.net-buttons/js/buttons.colVis")(window, $);
require("datatables.net-buttons/js/buttons.html5")(window, $);
require("datatables.net-buttons/js/buttons.print")(window, $);
require("datatables.net-rowgroup-bs5")(window, $);

$.extend($.fn.dataTable.defaults, {
  processing: true,
  serverSide: true,
  responsive: true,
  scrollX: false,
  language: {
    processing: '<i class="fas fa-sync-alt fa-spin fa-2x fa-fw" aria-hidden="true"></i>',
    zeroRecords: "No matching records found.",
    infoEmpty: "No entries to show.",
    lengthMenu: "Show _MENU_ entries",
    info: "Showing _START_ to _END_ of _TOTAL_ entries",
    paginate: {
      first: "<<",
      previous: "<",
      next: ">",
      last: ">>"
    }
  }
});

const DATATABLE_SELECTOR = ".datatables";
const DATATABLE_HEADER_SELECTOR = "thead > tr > th";
const DATAFILTER_INPUT_SELECTOR = ".search-input";
const DATAFILTER_SELECT_SELECTOR = ".dropdown-selected-filter-item";
const DATAFILTER_PARENT_SELECT_SELECTOR = ".search-filter";

const dataTables = [];
let collapsedGroups = {};
let topGroup = "";

const findDataTable = (tableId: string) => {
  dataTables.find((dataTable) => {
    tableId == $(dataTable.table().container()).find("table").attr("id");
  });
};

const onInput = (event, dataTable) => {
  const inputElement = event.currentTarget;
  const column = inputElement.dataset.column;

  if (column) {
    const value = inputElement.value;
    const columnSelector = `${column}:name`;

    dataTable.column(columnSelector).search(value).draw(false);
  } else {
    console.warn("No data-column attribute defined for input filter.");
  }
};

const initializeInputFilter = () => {
  const inputElement = $(DATAFILTER_INPUT_SELECTOR);

  if (inputElement) {
    const table = inputElement.data("table");
    const dataTable = findDataTable(table) || dataTables[0];
    let searchTimeout: NodeJS.Timeout | undefined;

    if (dataTable) {
      inputElement.keyup((event) => {
        // Ignore "Tab" as this was causing the keyup event to trigger before the timeout
        if (event.keyCode != "9") {
          if (searchTimeout != undefined) {
            clearTimeout(searchTimeout);
          }
          searchTimeout = setTimeout(() => {
            searchTimeout = undefined;
            onInput(event, dataTable);
          }, 400);
        }
      });
    } else {
      console.warn(`Could not find DataTable for Input Filter.`);
    }
  } else {
    inputElement.prop("disabled", true);
    console.warn("No input filter defined");
  }
};

const onSelect = (event, dataTable) => {
  event.preventDefault();

  const selectedElement = event.currentTarget;
  const column = selectedElement.dataset.column;

  if (column) {
    const value = selectedElement.dataset.search;
    const columnSelector = `${column}:name`;
    const parentElement = $(selectedElement).parents("ul").siblings(".dropdown-toggle").find(".dropdown--selected-filter");

    parentElement.html(selectedElement.innerHTML);
    parentElement.attr("title", selectedElement.text);

    dataTable.column(columnSelector).search(value).draw(false);
  } else {
    console.warn("No data-column attribute defined for select filter.");
  }
};

const initializeSelectFilter = () => {
  const selectedElement = $(DATAFILTER_SELECT_SELECTOR);
  const parentElement = selectedElement.closest(DATAFILTER_PARENT_SELECT_SELECTOR);

  if (selectedElement) {
    const table = parentElement.data("table");
    const dataTable = findDataTable(table) || dataTables[0];

    if (dataTable) {
      selectedElement.click((event) => onSelect(event, dataTable));
    } else {
      console.warn(`Could not find DataTable for Select Filter.`);
    }
  } else {
    console.warn("No select filter defined");
  }
};

const initializeDataFilters = () => {
  console.debug("Initializing DataFilters.");

  initializeInputFilter();
  initializeSelectFilter();

  console.debug("DataFilters initialized.");
};

const initializeDataTable = (dataTableElement: HTMLTableElement) => {
  const canViewAmount = dataTableElement.dataset?.can_view_amount === "true";
  const dataTableSource = dataTableElement.dataset?.source;
  const dataTableGroup = dataTableElement.dataset?.group;
  const dataGroups = [];

  if (dataTableGroup) {
    dataTableGroup?.split(",").forEach((dataGroup) => {
      dataGroups.push(dataGroup);
    });
  }

  if (dataTableSource) {
    const dataColumns = [];
    let invisibleColumnsCount = 0;

    dataTableElement.querySelectorAll(DATATABLE_HEADER_SELECTOR).forEach((datatableHeaderElement) => {
      const columnName = datatableHeaderElement.dataset.column;

      if (datatableHeaderElement.dataset.visible === "false") invisibleColumnsCount++;

      if (columnName) {
        dataColumns.push({ data: columnName, name: columnName });
      } else {
        console.warn("No data-column attribute defined for DataTable Header.");
      }
    });

    let dataTableConfig = {
      ajax: {
        url: dataTableSource
      },
      columns: dataColumns
    };

    if (dataGroups.length >= 1) {
      const projects: unknown[] = [];
      const journalEntries: unknown[] = [];
      const dataSrc =
        dataGroups[0] === "project_code"
          ? (row) => {
              const project = JSON.parse(row.project);
              const foundIndex = projects.findIndex((p) => p.id === project.id);

              if (foundIndex !== -1) {
                projects[foundIndex] = project;
              } else {
                projects.push(project);
              }

              return row.project_code;
            }
          : dataGroups[0] === "reference_document"
          ? (row) => {
              const journalEntry = JSON.parse(row.journal_entry);
              const foundIndex = journalEntries.findIndex((je) => je.id === journalEntry.id);

              if (foundIndex !== -1) {
                journalEntries[foundIndex] = journalEntry;
              } else {
                journalEntries.push(journalEntry);
              }

              return `${row.reference_document},${row.project_id},${row.date_created}`;
            }
          : dataGroups;
      dataTableConfig = {
        ...dataTableConfig,
        rowGroup: {
          dataSrc,
          startRender: (rows, group, level) => {
            let all;
            let top;

            if (level == 0) {
              top = group;
              all = group;
            } else {
              if (!!collapsedGroups[top]) {
                return;
              }
              all = top + group;
            }

            const collapsed = !!collapsedGroups[all];

            rows.nodes().each((r) => {
              r.style.display = collapsed ? "none" : "";
            });

            // For reports
            if (projects.length) {
              rows.nodes().each((r) => {
                $(r).find("td").eq(1).css("visibility", "hidden"); // index 1 - column 2 in table
                $(r).find("td").eq(2).css("visibility", "hidden"); // index 2 - column 3 in table
                $(r).find("td").eq(3).css("visibility", "hidden"); // index 3 - column 4 in table
                $(r).find("td").eq(4).css("visibility", "hidden"); // index 4 - column 5 in table
                $(r).find("td").eq(5).css("visibility", "hidden"); // index 5 - column 6 in table
                $(r).find("td").eq(6).css("visibility", "hidden"); // index 6 - column 7 in table
                $(r).find("td").eq(7).css("visibility", "hidden"); // index 7 - column 8 in table
              });
              const project: any = projects.find((p) => p.code === group);
              const floatVal = (v) => {
                if (typeof v === "string") {
                  if (v.includes("-")) {
                    return parseFloat(v.replace(/[^\d.-]/g, ""));
                  } else {
                    return parseFloat(v.replace(/[^\d.]/g, ""));
                  }
                } else if (typeof v === "number") {
                  return v;
                } else {
                  return 0.0;
                }
              };
              let totalBudgetedAmount = rows
                .data()
                .pluck("total_budgeted_amount")
                .map((el) => $(el).find("span").text())
                .reduce((a, b) => floatVal(a) + floatVal(b), 0);
              let totalActualCost = rows
                .data()
                .pluck("total_actual_cost")
                .map((el) => $(el).find("span").text())
                .reduce((a, b) => floatVal(a) + floatVal(b), 0);
              let totalActualCostWithinPeriod = rows
                .data()
                .pluck("total_actual_cost_within_period")
                .map((el) => $(el).find("span").text())
                .reduce((a, b) => floatVal(a) + floatVal(b), 0);

              if (project.hasOwnProperty("grade")) {
                totalBudgetedAmount = totalBudgetedAmount - project.timeliness_total_budgeted_amount;
                totalActualCost = totalActualCost - project.timeliness_total_actual_cost;
              }
              let actualCostBalance = totalActualCost - totalActualCostWithinPeriod;
              let costVariance = totalActualCost - totalBudgetedAmount;
              const variancePercentage = totalBudgetedAmount > 0 ? `${((costVariance / totalBudgetedAmount) * 100).toFixed(2)}%` : "0%";
              totalBudgetedAmount = `₱${totalBudgetedAmount.toLocaleString("en-PH", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;
              totalActualCost = `₱${totalActualCost.toLocaleString("en-PH", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;
              totalActualCostWithinPeriod = `₱${totalActualCostWithinPeriod.toLocaleString("en-PH", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;
              actualCostBalance = `₱${actualCostBalance.toLocaleString("en-PH", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;
              costVariance = `₱${costVariance.toLocaleString("en-PH", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;

              const row = $("<tr style='cursor: pointer'/>")
                .append(`<td class='text-truncate' title='${project?.code} (${rows.count()})' data-bs-toggle='tooltip'>${project?.code}</td>`)
                .append(`<td class='text-truncate' title='${project?.name}' data-bs-toggle='tooltip'>${project?.name}</td>`)
                .append(`<td class='text-truncate' title='${project?.block}' data-bs-toggle='tooltip'>${project?.block}</td>`)
                .append(`<td class='text-truncate' title='${project?.lot}' data-bs-toggle='tooltip'>${project?.lot}</td>`)
                .append(`<td class='text-truncate' title='${project?.manager}' data-bs-toggle='tooltip'>${project?.manager}</td>`)
                .append(`<td class='text-truncate' title='${project?.engineer}' data-bs-toggle='tooltip'>${project?.engineer}</td>`)
                .append(`<td class='text-truncate' title='${project?.foreman}' data-bs-toggle='tooltip'>${project?.foreman}</td>`)
                .append(`<td class='text-truncate' title='${project?.completion}' data-bs-toggle='tooltip'>${project?.completion}</td>`)
                .append(`<td class='text-truncate' title='${totalBudgetedAmount}' data-bs-toggle='tooltip'>${totalBudgetedAmount}</td>`);

              // Check if project has property 'grade', which means it's from project performance report
              if (project.hasOwnProperty("grade")) {
                let weight = rows
                  .data()
                  .pluck("weight")
                  .reduce((a, b) => floatVal(a) + floatVal(b), 0);
                weight = `${weight * 100}%`;
                let weightedGrade = rows
                  .data()
                  .pluck("weighted_grade")
                  .reduce((a, b) => floatVal(a) + floatVal(b), 0);
                weightedGrade = `${weightedGrade}%`;
                row
                  .append(`<td class='text-truncate' title='${totalActualCost}' data-bs-toggle='tooltip'>${totalActualCost}</td>`)
                  .append(`<td class='text-truncate' title='${costVariance}' data-bs-toggle='tooltip'>${costVariance}</td>`)
                  .append(`<td class='text-truncate' title='${variancePercentage}' data-bs-toggle='tooltip'>${variancePercentage}</td>`)
                  .append(`<td class='text-truncate' title='' data-bs-toggle='tooltip'></td>`)
                  .append(`<td class='text-truncate' title='${weight}' data-bs-toggle='tooltip'>${weight}</td>`)
                  .append(`<td class='text-truncate' title='${weightedGrade}' data-bs-toggle='tooltip'>${weightedGrade}</td>`);
              } else {
                row
                  .append(`<td class='text-truncate' title='${actualCostBalance}' data-bs-toggle='tooltip'>${actualCostBalance}</td>`)
                  .append(
                    `<td class='text-truncate' title='${totalActualCostWithinPeriod}' data-bs-toggle='tooltip'>${totalActualCostWithinPeriod}</td>`
                  )
                  .append(`<td class='text-truncate' title='${totalActualCost}' data-bs-toggle='tooltip'>${totalActualCost}</td>`)
                  .append(`<td class='text-truncate' title='${costVariance}' data-bs-toggle='tooltip'>${costVariance}</td>`)
                  .append(`<td class='text-truncate' title='${variancePercentage}' data-bs-toggle='tooltip'>${variancePercentage}</td>`);
              }

              return row.attr("data-name", all).toggleClass("collapsed", collapsed);
            }

            // For journal entries
            if (journalEntries.length) {
              rows.nodes().each((r) => {
                $(r).find("td").eq(0).css("visibility", "hidden"); // index 0 - column 1 in table
                $(r).find("td").eq(1).css("visibility", "hidden"); // index 1 - column 2 in table
                $(r).find("td").eq(2).css("visibility", "hidden"); // index 2 - column 3 in table
                $(r).find("td").eq(6).css("visibility", "hidden"); // index 6 - column 7 in table
                $(r).find("td").eq(7).css("visibility", "hidden"); // index 7 - column 8 in table
              });
              const groups = group.split(",");
              const projectId = +groups[1];
              const date_created = groups[2];
              const journalEntry: any = journalEntries.find(
                (je) => je.reference_document?.code === groups[0] && +je.project_id === projectId && je.date_created === date_created
              );

              return $("<tr style='cursor: pointer'/>")
                .append(`<td class='text-truncate' title='${journalEntry?.date}' data-bs-toggle='tooltip'>${journalEntry?.date}</td>`)
                .append(
                  `<td class='text-truncate' title='${journalEntry?.reference_document?.code}' data-bs-toggle='tooltip'>${journalEntry?.reference_document?.link}</td>`
                )
                .append(`<td class='text-truncate' title='${journalEntry?.code}' data-bs-toggle='tooltip'>${journalEntry?.code}</td>`)
                .append(`<td/>`)
                .append(`<td/>`)
                .append(`<td/>`)
                .append(
                  `<td class='text-truncate' title='${journalEntry?.remarks || ""}' data-bs-toggle='tooltip'>${journalEntry?.remarks || ""}</td>`
                )
                .append(`<td class='text-truncate' title='${journalEntry?.supplier}' data-bs-toggle='tooltip'>${journalEntry?.supplier}</td>`)
                .attr("data-name", all)
                .toggleClass("collapsed", collapsed);
            }

            return $("<tr/>")
              .append(`<td colspan=${dataColumns.length - invisibleColumnsCount}>${group} (${rows.count()})</td>`)
              .attr("data-name", all)
              .toggleClass("collapsed", collapsed);
          }
        }
      };
    }

    // Export only visible columns in transactions based on user role
    const exportOptions = () => {
      if (location.href.includes("/admin/transactions/")) {
        if (canViewAmount) return {};

        return {
          exportOptions: {
            columns: ":visible"
          }
        };
      }
    };

    const dataTable = $(dataTableElement).DataTable({
      ...dataTableConfig,
      dom: "<lB<t>ip>",
      buttons: [
        {
          extend: "excelHtml5",
          charset: "UTF-8",
          bom: true,
          ...exportOptions(),
          className: "button button--primary button--xsmall",
          text: "Export Excel"
        },
        {
          extend: "csvHtml5",
          charset: "UTF-8",
          bom: true,
          ...exportOptions(),
          className: "button button--primary button--xsmall",
          text: "Export CSV"
        }
      ],
      initComplete: (settings, json) => {
        if (json.data[0]?.actions == "") {
          dataTable.column("actions:name").visible(false);
        }
      }
    });

    if (!findDataTable(dataTableElement.id)) {
      dataTables.push(dataTable);
    }
  } else {
    console.warn("No data-source defined for DataTable.");
  }
};

const initializeDataTables = () => {
  console.debug("Initializing DataTables.");

  const dataTableElements = document.querySelectorAll(DATATABLE_SELECTOR);

  if (dataTables.length === 0 && dataTableElements.length !== 0) {
    dataTableElements.forEach((dataTableElement) => {
      initializeDataTable(dataTableElement);
    });
  } else {
    console.warn("No DataTables defined.");
  }

  console.debug("DataTables initialized.");
};

const cleanUpDataTables = () => {
  while (dataTables.length !== 0) {
    dataTables.pop().destroy();
  }
};

const initialize = () => {
  try {
    initializeDataTables();
    initializeDataFilters();

    $(DATATABLE_SELECTOR).on("click", "tr.dtrg-start", function () {
      const name = $(this).data("name");
      collapsedGroups[name] = !collapsedGroups[name];
      dataTables[0]?.draw(false);
    });
  } catch (error) {
    console.error(error);
  }
};

const cleanUp = () => {
  try {
    console.debug("Clean up DataTables.");
    cleanUpDataTables();
  } catch (error) {
    console.error(error);
  }
};

document.addEventListener("turbolinks:load", () => {
  initialize();
});

document.addEventListener("turbolinks:before-cache", () => {
  cleanUp();
});
